import React, { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { media } from "@atlaskit/primitives";
import { token } from "@atlaskit/tokens";

import { Picture } from "@/components/wpl/image/src";
import Link from "@/components/wpl/link/src";
import {
    ProductKeyTypes,
    TypographyStyles
} from "@/libraries/wpl/foundations/src";
import { Cross, DataEventProps } from "@/libraries/wpl/foundations/src";

export interface BannerProps {
    identifier: string;
    text: React.ReactNode;
    linkUrl: string;
    linkText: string;
    productKey: ProductKeyTypes;
    dataEventProps?: DataEventProps;
}

export const Adg4BannerAlert = (bannerInfo: BannerProps) => {
    const intl = useIntl();

    const bannerStorageItem = `BannerAlert_dismiss-${bannerInfo.identifier}`;

    const closeTranslatedLabel = intl.formatMessage({
        id: "adg4-banner-alert.close.aria-label",
        defaultMessage: "Close"
    });

    const closeIconDataEventProps: DataEventProps = {
        "data-product-key": bannerInfo.productKey,
        "data-event-component": "button",
        "data-event-container": "announcementBanner",
        "data-event": "clicked",
        "data-label": closeTranslatedLabel,
        "data-label-english": "Close"
    };

    const [isHidden, setIsHidden] = useState<boolean>(true);

    useEffect(() => {
        const dismissValue = localStorage.getItem(bannerStorageItem);

        if (dismissValue !== "1") {
            setIsHidden(false);
        }
    }, [bannerStorageItem]);

    return (
        <TypographyStyles>
            <div
                css={containerStyles}
                data-dismissed={isHidden ? "true" : undefined}
            >
                <div css={contentStyles}>
                    <div css={mainLogoStyles}>
                        <Picture
                            image={{
                                src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/bannerAlert_team24_logo_.svg",
                                alt: intl.formatMessage({
                                    id: "adg4-banner-alert.logoAltTxt",
                                    defaultMessage:
                                        "Atlassian team'24 event logo"
                                })
                            }}
                        />
                    </div>
                    <div css={lineStyle} className="line" />
                    <p css={pStyle} className="body-sm">
                        {bannerInfo.text}
                    </p>
                    <Link
                        text={bannerInfo.linkText}
                        href={bannerInfo.linkUrl}
                        type="basic"
                        dataEventProps={bannerInfo.dataEventProps}
                        doesArrowAnimate={true}
                    />
                    <button
                        aria-label={closeTranslatedLabel}
                        css={closeIconStyles}
                        onClick={() => {
                            setIsHidden(true);
                            localStorage.setItem(bannerStorageItem, "1");
                        }}
                        {...closeIconDataEventProps}
                    >
                        <Cross size="24" />
                    </button>
                </div>
                <div
                    css={[accentImagesStyle, leftImageStyle]}
                    className="left-image"
                >
                    <Picture
                        className="mobile"
                        css={accentImgMobileStyle}
                        image={{
                            src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/alert-left-graphic.svg",
                            alt: ""
                        }}
                    />
                    <Picture
                        className="desktop"
                        css={accentImgDesktopStyle}
                        image={{
                            src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/bannerAlert_team24_bg_l_with_lines.svg",
                            alt: ""
                        }}
                    />
                </div>
                <div
                    css={[accentImagesStyle, rightImageStyle]}
                    className="right-image"
                >
                    <Picture
                        className="mobile"
                        css={accentImgMobileStyle}
                        image={{
                            src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/alert-right-graphic.svg",
                            alt: ""
                        }}
                    />
                    <Picture
                        className="desktop"
                        css={accentImgDesktopStyle}
                        image={{
                            src: "https://wac-cdn.atlassian.com/misc-assets/webp-images/bannerAlert_team24_bg_r_.svg",
                            alt: ""
                        }}
                    />
                </div>
            </div>
        </TypographyStyles>
    );
};

const mainLogoStyles = css({
    [media.below.sm]: {
        paddingInlineStart: "8px"
    }
});

const containerStyles = css({
    backgroundColor: "#E9F2FE",
    position: "relative",
    padding: 0,
    margin: 0,
    display: "flex",
    zIndex: 1,
    paddingBlockEnd: "2px",
    backgroundImage: `linear-gradient(to right, 
                                    #BF63F3 10%, 
                                    #09326C 15.63%, 
                                    #1868DB 33.27%, 
                                    #AE2A19 40.62%, 
                                    #FFA900 63.28%, 
                                    #164B35 78.23%, 
                                    #82B536 100%)`,
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% 2px",
    backgroundPosition: "bottom",

    [media.above.md]: {
        maxHeight: "80px"
    },
    [`&[data-dismissed="true"]`]: {
        display: "none"
    }
});

const accentImagesStyle = css({
    insetBlockEnd: 0,
    position: "absolute",
    display: "flex"
});

const accentImgMobileStyle = css({
    [media.above.md]: {
        display: "none"
    }
});
const accentImgDesktopStyle = css({
    [media.below.md]: {
        display: "none"
    }
});

const leftImageStyle = css({
    insetInlineStart: 0
});

const rightImageStyle = css({
    insetInlineEnd: 0
});

const contentStyles = css({
    width: "100%",
    maxHeight: "375px",
    maxWidth: "1240px",
    margin: `${token("space.300")} ${token("space.400")}`,
    display: "flex",
    flexDirection: "column",
    gap: "15px",
    [media.above.md]: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: token("space.400"),
        margin: `${token("space.100")} auto`
    }
});

const pStyle = css({
    maxWidth: "355px",
    margin: 0
});

const lineStyle = css({
    borderBlockStart: "1px solid #6C6F77",
    width: 51,
    position: "relative",
    [media.above.md]: {
        borderInlineStart: "1px solid #6C6F77",
        borderBlockStart: "none",
        height: 51,
        width: 0
    }
});

const closeIconStyles = css({
    appearance: "none",
    background: "transparent",
    border: 0,
    cursor: "pointer",
    padding: 0,
    position: "absolute",
    insetBlockStart: token("space.300"),
    insetInlineEnd: token("space.300"),
    [media.above.md]: {
        insetInlineEnd: "95px"
    },
    svg: {
        color: "#44546F"
    }
});
